/**
 * @callback env
 * @param  {string=}  environment
 * @return {Env|boolean|string}
 */

/**
 * @typedef {Object} Env
 * @property {function(): string} environment
 * @property {function(string): boolean} environment
 * @property {function(): boolean} isDev
 * @property {function(): boolean} isProd
 * @property {function(): boolean} mayTrack
 * @property {function(string=): string} apiEndpoint
 * @property {function(string=): string} endpoint
 * @property {function(string=): string} gotoEndpoint
 * @property {function(): string} pixelId
 */

const META_PIXEL = '1166962698653071';
const META_PIXEL_DEV = '1346081600284546';

const API_ENDPOINT = 'https://api.djinly.app/v1';
const API_ENDPOINT_DEV = 'https://api.dev.djinly.app/v1';

const REDIRECT_ENDPOINT = 'https://djinly.app';
const REDIRECT_ENDPOINT_DEV = 'https://dev.djinly.app';

export class Env
{
    /**
     * Whether the client has a development cookie set.
     *
     * @protected
     * @type {boolean}
     */
    hasDevCookie = false;

    /**
     * Whether the client has a Do Not Track cookie set.
     *
     * @protected
     * @type {boolean}
     */
    hasDoNotTrackCookie = false;

    /**
     * Create a new Env instance.
     *
     * @public
     * @return {void}
     */
    constructor()
    {
        this.hasDevCookie = this.hasCookie('_dev');
        this.hasDoNotTrackCookie = this.hasCookie('_dnt');
    }

    /**
     * Get or check the current environment.
     *
     * @public
     * @param  {string=}  environment
     * @return {string|boolean}
     */
    environment(environment = null)
    {
        const env = this.hasDevCookie
            ? 'development' : 'production';

        if (environment) {
            return env.includes(environment);
        }

        return env;
    }

    /**
     * Determine if the current environment is development.
     *
     * @public
     * @return {boolean}
     */
    isDev()
    {
        return this.environment('development');
    }

    /**
     * Determine if the current environment is production.
     *
     * @public
     * @return {boolean}
     */
    isProd()
    {
        return this.environment('production');
    }

    /**
     * Determine if the client may be tracked.
     *
     * @public
     * @return {boolean}
     */
    mayTrack()
    {
        return ! this.hasDoNotTrackCookie;
    }

    /**
     * Return the API endpoint for the current environment.
     *
     * @public
     * @param  {string=}  path
     * @return {string}
     */
    apiEndpoint(path = null)
    {
        const endpoint = this.isProd()
            ? API_ENDPOINT : API_ENDPOINT_DEV;

        if (path && path.length) {
            path = path.replace(/^\/+/, '');
            return `${ endpoint }/${ path }`;
        }

        return endpoint;
    }

    /**
     * Return the general redirect endpoint for the current environment.
     *
     * @public
     * @param  {string=}  path
     * @return {string}
     */
    endpoint(path = null)
    {
        const endpoint = this.isProd()
            ? REDIRECT_ENDPOINT : REDIRECT_ENDPOINT_DEV;

        if (path && path.length) {
            path = path.replace(/^\/+/, '');
            return `${ endpoint }/${ path }`;
        }

        return endpoint;
    }

    /**
     * Return the general redirect endpoint for the current environment.
     *
     * @public
     * @param  {string=}  path
     * @return {string}
     */
    gotoEndpoint(path = null)
    {
        return this.endpoint(path ? `goto/${ path }` : 'goto');
    }

    /**
     * Return the Meta Pixel ID for the current environment.
     *
     * @public
     * @return {string}
     */
    pixel()
    {
        return this.isProd()
            ? META_PIXEL : META_PIXEL_DEV;
    }

    /**
     * Determine if a cookie with the given name is set.
     *
     * @protected
     * @param  {string}  name
     * @return {boolean}
     */
    hasCookie(name)
    {
        const pattern = new RegExp(
            '(?:^|; )' + name.replace(/[$()*+.?[\\\]^{|}-]/g, '\\$&') + '=([^;]*)'
        );

        const value = document.cookie.match(pattern)?.[1];

        return value !== undefined;
    }
};

export default (window) => {
    const env = new Env();

    window.env = (environment = null) => {
        if (environment !== null) {
            return env.environment(environment);
        }

        return env;
    };
};
